import { type ReactNode } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardContentDescription,
} from '@/components/Card'
import { Button } from '@/components/Button'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { Tooltip } from '@/components/Tooltip'
import type { Id } from '@/infra/id'
import styles from './RoomCard.module.css'

export interface RoomCardProps {
  isActive: boolean
  hasActiveParticipants: boolean
  participants: RoomParticipant[]
  isJoinPending: boolean
  isRoomClosed: boolean
  onJoin: () => unknown
  buttonGroup?: ReactNode
  titleContent?: ReactNode
  descriptionContent?: ReactNode
  progressBar?: ReactNode
  testId?: string
}

export function RoomCard({
  isActive,
  hasActiveParticipants,
  participants,
  isJoinPending,
  isRoomClosed,
  onJoin,
  titleContent,
  descriptionContent,
  buttonGroup,
  progressBar,
  testId,
}: RoomCardProps) {
  if (!isActive) {
    return null
  }
  return (
    <Card testId={testId}>
      <CardHeader>
        <CardTitle>{titleContent || 'Video Call'}</CardTitle>
      </CardHeader>
      <CardContent>
        {progressBar && (
          <>
            {progressBar}
            <br />
          </>
        )}
        <CardContentDescription>
          {descriptionContent || 'Join the video call by clicking below.'}
        </CardContentDescription>
      </CardContent>
      {hasActiveParticipants && (
        <CardContent>
          <RoomParticipants participants={participants} />
        </CardContent>
      )}
      <CardContent>
        <div className={styles.buttonGroup}>
          <Button
            label="Join Call"
            variant="primary"
            size="wide-slim"
            isDisabled={isJoinPending || isRoomClosed}
            isPending={isJoinPending}
            onPress={onJoin}
          />
          {buttonGroup}
        </div>
      </CardContent>
    </Card>
  )
}

export interface RoomParticipant extends AvatarSubject {
  publicId: Id
}

export interface RoomParticipantsProps {
  participants: RoomParticipant[]
}

export function RoomParticipants({ participants }: RoomParticipantsProps) {
  return (
    <div className={styles.participants}>
      {participants.length > 4 ? (
        <span>{participants.length} participants</span>
      ) : (
        participants.map((p) => (
          <Tooltip
            content={`${p.displayName}`}
            placement="bottom"
            key={p.publicId}
          >
            <div className={styles.participant}>
              <Avatar size={24} subject={p} />
            </div>
          </Tooltip>
        ))
      )}
    </div>
  )
}
